import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { CaretRightOutlined, FullscreenOutlined, PauseOutlined } from '@ant-design/icons';
import './AutoDemo.css';

// Coded product demo in place of a screen recording: scenes are drawn on a fixed 960 × 540 stage that is scaled
// to the space available, and one clock drives them, so the demo plays, pauses, jumps between chapters and loops
// like a video. A scene renders from its own elapsed time `t` (no CSS keyframes), which keeps every frame
// reproducible and lets the texts come from the interface language.
export const STAGE_W = 960;
export const STAGE_H = 540;

const prefersReducedMotion = () => typeof window !== 'undefined' && window.matchMedia?.('(prefers-reduced-motion: reduce)')?.matches;

// controls={false} with autoPlay={false} and a startAt gives a still poster frame of the demo.
const AutoDemo = ({ scenes, label, autoPlay = true, loop = true, controls = true, startAt = 0, onExpand, labels = {}, className = '' }) => {
    const starts = useMemo(() => scenes.reduce((acc, scene, i) => [...acc, i ? acc[i - 1] + scenes[i - 1].duration : 0], []), [scenes]);
    const total = starts[starts.length - 1] + scenes[scenes.length - 1].duration;
    // With reduced motion the demo does not run by itself: each chapter shows its finished state.
    const [reduced] = useState(prefersReducedMotion);
    const [time, setTime] = useState(startAt);
    const [playing, setPlaying] = useState(autoPlay && !reduced);
    const [scale, setScale] = useState(1);
    const rootRef = useRef(null);

    useEffect(() => {
        const node = rootRef.current;
        if (!node) return undefined;
        const fit = () => setScale(node.clientWidth / STAGE_W || 1);
        fit();
        const resize = typeof ResizeObserver !== 'undefined' ? new ResizeObserver(fit) : null;
        resize?.observe(node);
        return () => resize?.disconnect();
    }, []);

    const timeRef = useRef(startAt);
    const setClock = useCallback((value) => { timeRef.current = value; setTime(value); }, []);

    // The browser stops animation frames in hidden tabs, so a demo nobody sees costs nothing; the hosts unmount
    // it when their dialog closes.
    useEffect(() => {
        if (!playing) return undefined;
        let frame;
        let last = performance.now();
        const tick = (now) => {
            const delta = document.hidden ? 0 : Math.min(now - last, 100);
            last = now;
            const next = timeRef.current + delta;
            if (next < total) setClock(next);
            else if (loop) setClock(next % total);
            else { setClock(total - 1); setPlaying(false); return; }
            frame = requestAnimationFrame(tick);
        };
        frame = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(frame);
    }, [playing, total, loop, setClock]);

    let index = starts.findIndex((start, i) => time < start + scenes[i].duration);
    if (index < 0) index = scenes.length - 1;
    const scene = scenes[index];
    const local = reduced && !playing && controls ? scene.duration : time - starts[index];
    const seek = useCallback((i) => setClock(starts[i]), [starts, setClock]);

    return (
        <div className={`auto-demo ${className}`} ref={rootRef} role="group" aria-label={label}>
            <div className="auto-demo__viewport" style={{ height: STAGE_H * scale }}>
                <div className="auto-demo__stage" style={{ transform: `scale(${scale})` }} aria-hidden="true">
                    {scene.render({ t: local, duration: scene.duration })}
                </div>
            </div>
            {controls && (
            <div className="auto-demo__bar">
                <button type="button" className="auto-demo__play" onClick={() => setPlaying((p) => !p)}
                    aria-label={playing ? (labels.pause || 'Pause') : (labels.play || 'Play')}>
                    {playing ? <PauseOutlined /> : <CaretRightOutlined />}
                </button>
                <div className="auto-demo__chapters">
                    {scenes.map((s, i) => {
                        const fill = i < index ? 1 : i > index ? 0 : Math.min(1, (time - starts[i]) / s.duration);
                        return (
                            <button key={s.id} type="button" className={`auto-demo__chapter${i === index ? ' is-current' : ''}`}
                                style={{ flexGrow: s.duration }} onClick={() => seek(i)} aria-label={s.title} title={s.title}
                                aria-current={i === index ? 'step' : undefined}>
                                <span style={{ width: `${(reduced && !playing ? (i <= index ? 1 : 0) : fill) * 100}%` }} />
                            </button>
                        );
                    })}
                </div>
                {onExpand && (
                    <button type="button" className="auto-demo__expand" onClick={onExpand} aria-label={labels.expand || 'Expand'}>
                        <FullscreenOutlined />
                    </button>
                )}
            </div>
            )}
        </div>
    );
};

export default AutoDemo;

// Animation helpers for scenes: all take the scene time t in milliseconds.
export const appear = (t, at, length = 320) => {
    const p = Math.max(0, Math.min(1, (t - at) / length));
    const eased = 1 - (1 - p) ** 3;
    return { opacity: eased, transform: `translateY(${(1 - eased) * 8}px)` };
};
export const typed = (text, t, at, perChar = 38) => text.slice(0, Math.max(0, Math.floor((t - at) / perChar)));
export const typingDone = (text, at, perChar = 38) => at + text.length * perChar;
